import axios from 'axios';

/**
 * Upload a file straight to S3 with a presigned POST, so the bytes never pass through our server.
 *
 * The one place every browser upload goes now. It asks the BFF's /upload-url route for a form
 * (which validates the bucket, extension and size, composes the key server-side, and -- via the
 * form's content-length-range -- makes S3 itself enforce the cap), then POSTs the returned policy
 * fields plus the file, last, straight to S3. A bare axios instance so none of the app's auth
 * headers reach S3 and the multipart POST stays a simple CORS request.
 *
 * Returns the object key the server signed for; the caller records it (as a stored fileUrl) and
 * never trusts a key of its own composing.
 *
 * A PDF in the gallery bucket (a candidate's work sample) is always confirmed afterwards, even when
 * S3 answered, because confirm is where the server reads the file's first bytes: a file renamed to
 * .pdf is deleted there and the upload throws with the server's reason, so nothing records it.
 */
export async function uploadToS3Direct(
  bucket: string,
  file: File,
  opts: {
    fileName: string;
    maxBytes?: number;
    employerId?: string;
    /** 0 to 1 as the bytes go up; for a per-file progress bar. */
    onProgress?: (_fraction: number) => void;
  },
): Promise<string> {
  const { data } = await axios.post(`/api/s3/buckets/${bucket}/upload-url`, {
    fileName: opts.fileName,
    fileSizeBytes: file.size,
    ...(opts.maxBytes ? { maxBytes: opts.maxBytes } : {}),
    ...(opts.employerId ? { employerId: opts.employerId } : {}),
  });
  const { url, fields, key } = data as {
    url: string;
    fields: Record<string, string>;
    key: string;
  };

  const form = new FormData();
  Object.entries(fields).forEach(([k, v]) => form.append(k, v));
  form.append('file', file); // must be last for the POST policy

  const confirm = async (): Promise<boolean> => {
    const { data: check } = await axios.post(`/api/s3/buckets/${bucket}/upload-confirm`, {
      key,
      fileSizeBytes: file.size,
      ...(opts.employerId ? { employerId: opts.employerId } : {}),
    });
    return Boolean((check as { landed?: boolean })?.landed);
  };

  try {
    await axios.create().post(url, form, {
      onUploadProgress: opts.onProgress
        ? (e) => opts.onProgress?.(e.total ? Math.min(1, e.loaded / e.total) : 0)
        : undefined,
    });
  } catch (err) {
    // A response we can read (a 4xx from S3: too large, policy refused) is a real refusal.
    if ((err as { response?: unknown })?.response) throw err;
    // No readable response. A multipart POST is a CORS simple request, so the browser may have
    // delivered the file even though it would not show us S3's answer. Ask our server, which can
    // check S3 directly, before calling it failed and inviting a retry that duplicates the file.
    if (!(await confirm())) throw err;
    return key;
  }
  if (mustCheckContent(bucket, key) && !(await confirm())) {
    throw new Error('The upload could not be confirmed. Try again.');
  }
  return key;
}

/** Uploads the server must read back before they count: PDFs in the public gallery bucket. */
export const mustCheckContent = (bucket: string, key: string): boolean =>
  bucket === 'trymintly-companygallery' && key.toLowerCase().endsWith('.pdf');
